import { EventEmitter } from 'events'
import { ForthVM, Word } from '../forth'

const DIRECTION_TABLE = {
  'nw': 0,
  'n' : 1,
  'ne': 2,
  'w' : 3,
  'e' : 4,
  'sw': 5,
  's' : 6,
  'se': 7
}

class CAForthVM extends ForthVM {
  constructor () {
    super()
    this.cellInfo = {
      neighbors: [0, 0, 0,
                  0,    0,
                  0, 0, 1],
      self: 0
    }
    this.dictionary['c'] = new Word({
      name: 'c',
      body: () => {
        this.push(this.cellInfo.self)
      }
    })
    for (let dir in DIRECTION_TABLE) {
      this.dictionary[dir] = new Word({
        name: dir,
        body: () => {
          this.push(this.cellInfo.neighbors[DIRECTION_TABLE[dir]])
        }
      })
    }
    this.dictionary['neighbors'] = new Word({
      name: 'neighbors',
      body: () => {
        this.cellInfo.neighbors.forEach(n => {
          this.push(n)
        })
      }
    })
  }
  updateCellInfo ({self, neighbors}) {
    this.cellInfo.self = self
    this.cellInfo.neighbors = neighbors
  }
  makeRule (program) {
    let f = this.compile(program)
    let ruleTable = new Array(512).fill(0)
    for (let i = 0; i < 512; i++) {
      this.clear()
      let cells = (i).toString(2).padStart(9, '0').split('').map(v => parseInt(v))
      let self = cells.splice(4, 1)[0]
      this.updateCellInfo({self: self, neighbors: cells})
      f()
      ruleTable[i] = this.tos()
    }
    return {
      program: program,
      fun: (neighbors) => {
        let i = neighbors.reverse().reduce((a, v, i) => {
          return Math.pow(2, i) * v + a
        }, 0)
        return ruleTable[i]
      },
      table: ruleTable
    }
  }
}

export class TwoDimCellularAutomaton extends EventEmitter {
  // K = 2, r = 1
  constructor ({width = 600, height = 300}) {
    super()
    this.width = width
    this.height = height
    this.lattice = new Array(height).fill(0).map(() => new Array(width).fill(0))
    this.nextl = new Array(height).fill(0).map(() => new Array(width).fill(0))
    this.age = 0
    this.forth = new CAForthVM()
    this.rule = this.forth.makeRule(['neighbors', 'c', 'sum', 4, '>'])
  }
  initWorld ({density = 0.5}) {
    this.age = 0
    this.lattice.forEach(row => {
      row.forEach((v, i) => {
        row[i] = Math.random() > density ? 0 : 1
      })
    })
    this.updated()
    return this.lattice
  }
  oneStep () {
    let old = this.lattice
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        this.nextl[y][x] = this.rule.fun(this.neighborsAt(x, y))
      }
    }
    this.lattice = this.nextl
    this.nextl = old
    this.age += 1
    this.updated()
    return this.lattice
  }

  // Moore neighbors including self.
  neighborsAt (x, y) {
    let py = y === 0 ? this.height-1 : y-1
    let ny = y === (this.height-1) ? 0 : y+1
    let px = x === 0 ? this.width-1 : x-1
    let nx = x === (this.width-1) ? 0 : x+1
    return [
      this.lattice[py][px],this.lattice[py][ x],this.lattice[py][nx],
      this.lattice[ y][px],this.lattice[ y][ x],this.lattice[ y][nx],
      this.lattice[ny][px],this.lattice[ny][ x],this.lattice[ny][nx]
    ]
  }
  updated () {
    this.emit('updated', {
      lattice: this.lattice,
      age: this.age
    })
  }
  setRule (program) {
    this.rule = this.forth.makeRule(program)
  }
}


function lifeGameRule (self, neighbors) {
  let sum = neighbors.reduce((a,v) => a+v)
  if (self === 0) {
    return sum === 3 ? 1 : 0
  }
  return (sum === 3 || sum === 2) ? 1 : 0
}

// outer totalistic
function makeOuterTotalisticRule ({alive, dead}) {
  const ruleArray = [dead, alive]
  return (self, neighbors) => {
    return ruleArray[self][neighbors.reduce((a,v) => a+v)]
  }
}

export class OuterTotalisticAutomaton extends TwoDimCellularAutomaton {
  constructor (obj) {
    super(obj)
    this.rule = lifeGameRule
  }
  oneStep () {
    let old = this.lattice
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        this.nextl[y][x] = this.rule(this.lattice[y][x], this.neighborsAt(x, y))
      }
    }
    this.lattice = this.nextl
    this.nextl = old
    this.age += 1
    this.updated()
    return this.lattice
  }

  // Moore neighbors except self(x,y).
  // return one dimensional array.
  // top left to bottom right.
  neighborsAt (x, y) {
    let py = y === 0 ? this.height-1 : y-1
    let ny = y === (this.height-1) ? 0 : y+1
    let px = x === 0 ? this.width-1 : x-1
    let nx = x === (this.width-1) ? 0 : x+1
    return [
      this.lattice[py][px],this.lattice[py][ x],this.lattice[py][nx],
      this.lattice[ y][px],                     this.lattice[ y][nx],
      this.lattice[ny][px],this.lattice[ny][ x],this.lattice[ny][nx]
    ]
  }

  setRule (ruleFun) {
    this.rule = ruleFun
  }

  static makeOuterTotalisticRule ({alive = [0, 0, 1, 1, 0, 0, 0, 0, 0],
                                   dead  = [0, 0, 0, 1, 0, 0, 0, 0, 0]}) {
    return makeOuterTotalisticRule({alive, dead})
  }
}
